import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaSearch } from 'react-icons/fa'
import api from '../../../../helpers/apiRequester'
import styles from '../shared/mhlShared.module.scss'
import localStyles from './MilitaryHearingLossWebsiteVisitors.module.scss'
import { Pill } from '../shared/SourceTabs'
import type { VisitorHit, VisitorHitsResponse, VisitorRangeDays } from '../shared/mhlTypes'
import { formatDateTime, referralLabel, referralTone } from '../shared/mhlFormatting'
import { TablePagination } from '../shared/TablePagination'

const PAGE_SIZE = 25

interface Props {
    rangeDays: VisitorRangeDays
    /** Page-wide referral source filter: 'all' or a source such as 'sentinelforces'. */
    source: string
    /** Bumped by the parent's Refresh button. */
    reloadKey: number
}

const location = (hit: VisitorHit) => [hit.city, hit.region, hit.country].filter(Boolean).join(', ') || '—'

const pathOf = (url: string | null) => {
    if (!url) return null
    try {
        const u = new URL(url)
        return `${u.pathname}${u.search}` || '/'
    } catch {
        return url
    }
}

/** Every claim-site page load: who, when, where from, and what they were looking at. */
export const VisitorsTable = ({ rangeDays, source, reloadKey }: Props) => {
    const navigate = useNavigate()
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [appliedSearch, setAppliedSearch] = useState('')
    const [convertedOnly, setConvertedOnly] = useState(false)
    const [data, setData] = useState<VisitorHitsResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const loadGenRef = useRef(0)

    // Debounce the free-text search.
    useEffect(() => {
        const t = setTimeout(() => {
            setAppliedSearch(search.trim())
            setPage(1)
        }, 350)
        return () => clearTimeout(t)
    }, [search])

    useEffect(() => {
        setPage(1)
    }, [rangeDays, convertedOnly, source])

    const load = useCallback(async () => {
        const gen = ++loadGenRef.current
        setLoading(true)
        setError(null)
        try {
            const res = await api({
                url: '/military-hearing-loss-visitor-hits',
                data: {
                    days: Number(rangeDays),
                    page,
                    pageSize: PAGE_SIZE,
                    search: appliedSearch,
                    convertedOnly,
                    source: source === 'all' ? undefined : source,
                },
            })
            if (gen !== loadGenRef.current) return
            const body = res.data as VisitorHitsResponse
            if (!body?.success) {
                setError(body?.message || 'Failed to load visitors')
                setData(null)
                return
            }
            setData(body)
        } catch (err: any) {
            if (gen !== loadGenRef.current) return
            setError(err?.response?.data?.message || err?.message || 'Failed to load visitors')
            setData(null)
        } finally {
            if (gen === loadGenRef.current) setLoading(false)
        }
    }, [rangeDays, page, appliedSearch, convertedOnly, source])

    useEffect(() => {
        void load()
    }, [load, reloadKey])

    const hits = data?.hits || []

    return (
        <>
            <div className={styles.toolbar}>
                <div className={styles.searchWrap}>
                    <FaSearch className={styles.searchIcon} />
                    <input
                        className={styles.searchInput}
                        placeholder="Search IP, city, country, page URL or referrer"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        aria-label="Search visitors"
                    />
                </div>
                <label className={localStyles.filterChecks}>
                    <input type="checkbox" checked={convertedOnly} onChange={(e) => setConvertedOnly(e.target.checked)} />
                    Converted only
                </label>
                <span className={styles.resultCount}>
                    {data ? `${data.total.toLocaleString()} page loads · ${data.uniqueIps.toLocaleString()} unique IPs` : ''}
                </span>
            </div>

            {error && <div className={styles.errorBanner}>{error}</div>}

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Date / time</th>
                            <th>IP address</th>
                            <th>Location</th>
                            <th>Source</th>
                            <th>Page</th>
                            <th>Browser</th>
                            <th className="num">Visits (IP)</th>
                            <th>Converted</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && !data ? (
                            <tr>
                                <td colSpan={8} className={styles.emptyState}>
                                    Loading…
                                </td>
                            </tr>
                        ) : hits.length === 0 ? (
                            <tr>
                                <td colSpan={8} className={styles.emptyState}>
                                    No visits recorded in this period.
                                </td>
                            </tr>
                        ) : (
                            hits.map((hit) => {
                                const path = pathOf(hit.referrer)
                                return (
                                    <tr key={hit.id}>
                                        <td>
                                            <span className={styles.stackSecondary}>{formatDateTime(hit.visited_at)}</span>
                                        </td>
                                        <td>
                                            <span className={localStyles.mono}>{hit.ip_address}</span>
                                        </td>
                                        <td>
                                            <span className={styles.stackSecondary}>{location(hit)}</span>
                                        </td>
                                        <td>
                                            <div className={styles.stackCell}>
                                                <Pill tone={referralTone(hit.source)}>{referralLabel(hit.source)}</Pill>
                                                {hit.external_referrer && (
                                                    <span
                                                        className={`${styles.stackSecondary} ${localStyles.truncate} ${localStyles.truncateNarrow}`}
                                                        title={hit.external_referrer}
                                                    >
                                                        from {hit.external_referrer}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.stackCell}>
                                                {hit.referrer ? (
                                                    <a
                                                        href={hit.referrer}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className={`${styles.stackSecondary} ${localStyles.truncate}`}
                                                        title={hit.referrer}
                                                    >
                                                        {path}
                                                    </a>
                                                ) : (
                                                    <span className={styles.stackSecondary}>—</span>
                                                )}
                                                {hit.origin && hit.origin !== hit.referrer && (
                                                    <span
                                                        className={`${styles.stackSecondary} ${localStyles.truncate} ${localStyles.truncateNarrow}`}
                                                        title={hit.origin}
                                                    >
                                                        {hit.origin}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.stackCell}>
                                                <span className={styles.stackSecondary} title={hit.user_agent || undefined}>
                                                    {hit.agent?.label || '—'}
                                                </span>
                                                {hit.agent && <span className={styles.stackSecondary}>{hit.agent.device}</span>}
                                            </div>
                                        </td>
                                        <td className="num">
                                            <span className={styles.stackSecondary}>{hit.visit_count ?? '—'}</span>
                                        </td>
                                        <td>
                                            {hit.converted && hit.converted_client_id ? (
                                                <button
                                                    type="button"
                                                    className={localStyles.messageToggle}
                                                    onClick={() => navigate(`/military-hearing-loss/client/${hit.converted_client_id}`)}
                                                    title={`Matched by IP · ${hit.converted_case_ref || ''}`}
                                                >
                                                    <Pill tone="good" dot>
                                                        Signed up
                                                    </Pill>
                                                </button>
                                            ) : (
                                                <Pill tone="muted">Visitor</Pill>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>

                {data && (
                    <TablePagination
                        page={data.page}
                        totalPages={data.totalPages}
                        total={data.total}
                        pageSize={data.pageSize}
                        onChange={setPage}
                    />
                )}
            </div>
            <p className={localStyles.sectionNote} style={{ marginTop: 10 }}>
                "Signed up" means a public claim-form submission came from the same IP address. Shared or mobile
                networks can make this approximate.
            </p>
        </>
    )
}
